import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Select } from '../components/Select';
import { UserIcon } from '../components/Icons';

interface Contact {
  id: string;
  name: string;
  role: 'writer' | 'reader' | 'agent';
  photo: string;
}

export const NewMessagePage = () => {
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');

  // Mock contacts
  const contacts: Contact[] = [
    { id: '1', name: 'Sarah Johnson', role: 'agent', photo: '' },
    { id: '2', name: 'Michael Chen', role: 'writer', photo: '' },
    { id: '3', name: 'Emma Rodriguez', role: 'reader', photo: '' },
    { id: '4', name: 'David Park', role: 'reader', photo: '' },
    { id: '5', name: 'Jennifer Williams', role: 'agent', photo: '' },
    { id: '6', name: 'Robert Taylor', role: 'writer', photo: '' },
    { id: '7', name: 'Lisa Anderson', role: 'reader', photo: '' },
    { id: '8', name: 'James Martinez', role: 'agent', photo: '' },
  ];

  const getRoleLabel = (role: string): string => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const handleSend = () => {
    if (!selectedRecipient) {
      window.alert('Please select a recipient');
      return;
    }
    if (!subject.trim()) {
      window.alert('Please enter a subject');
      return;
    }
    if (!messageBody.trim()) {
      window.alert('Please enter a message');
      return;
    }

    const recipient = contacts.find(c => c.id === selectedRecipient);
    console.log('Sending message:', {
      to: recipient?.name,
      subject,
      message: messageBody,
    });

    window.alert(`Message sent to ${recipient?.name}!`);
    window.location.href = '/messages';
  };

  const handleCancel = () => {
    if (messageBody.trim() || subject.trim()) {
      if (window.confirm('Discard this message?')) {
        window.location.href = '/messages';
      }
    } else {
      window.location.href = '/messages';
    }
  };

  const selectedContact = contacts.find(c => c.id === selectedRecipient);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            New Message
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Send a message to another user
          </p>
        </div>

        {/* Recipient Selection */}
        <Card style={{ marginBottom: '24px' }}>
          <Select
            label="Recipient *"
            value={selectedRecipient}
            onChange={(e) => setSelectedRecipient(e.target.value)}
            options={[
              { value: '', label: 'Select a recipient...' },
              ...contacts.map(contact => ({
                value: contact.id,
                label: `${contact.name} (${getRoleLabel(contact.role)})`,
              })),
            ]}
            required
            helperText="Choose who you want to send a message to"
          />

          {selectedContact && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: theme.colors.backgroundAlt,
              borderRadius: theme.borderRadius.medium,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: theme.colors.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <UserIcon width={24} height={24} />
              </div>
              <div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  marginBottom: '2px',
                }}>
                  {selectedContact.name}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: theme.colors.textSecondary,
                }}>
                  {getRoleLabel(selectedContact.role)}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Subject */}
        <Card style={{ marginBottom: '24px' }}>
          <Input
            label="Subject *"
            placeholder="Enter subject line..."
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            helperText="Brief description of what your message is about"
          />
        </Card>

        {/* Message Body */}
        <Card style={{ marginBottom: '24px' }}>
          <Textarea
            label="Message *"
            placeholder="Write your message..."
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            rows={12}
            required
            helperText="Be clear and professional in your communication"
          />
          <div style={{
            marginTop: '8px',
            fontSize: '13px',
            color: theme.colors.textTertiary,
            textAlign: 'right',
          }}>
            {messageBody.length} characters
          </div>
        </Card>

        {/* Guidelines */}
        <Card style={{
          marginBottom: '32px',
          backgroundColor: theme.colors.primaryLight,
          border: `1px solid ${theme.colors.primary}`,
        }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
          }}>
            <div style={{ fontSize: '20px' }}>💡</div>
            <div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
                marginBottom: '8px',
              }}>
                Messaging Guidelines
              </div>
              <ul style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                margin: 0,
                paddingLeft: '20px',
                lineHeight: '1.6',
              }}>
                <li>Be professional and respectful in all communications</li>
                <li>Keep messages relevant to manuscripts, feedback, or queries</li>
                <li>Do not share personal contact information in initial messages</li>
                <li>Report any inappropriate or harassing messages immediately</li>
                <li>Response times may vary based on user availability</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
        }}>
          <Button
            variant="ghost"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!selectedRecipient || !subject.trim() || !messageBody.trim()}
          >
            Send Message
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};
